import React, { useState, useEffect, useMemo, useCallback } from 'react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faPrint, faSearch, faFileExcel, faSyncAlt, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import logoImage from '../assets/images/logo.jpg';
import QRCode from 'qrcode';
import { FixedSizeList as List } from 'react-window';
import AutoSizer from 'react-virtualized-auto-sizer';
import Fuse from 'fuse.js';

const Localdomlist = () => {
    const navigate = useNavigate();
    const [gdsrEntries, setGdsrEntries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [lastRefreshTime, setLastRefreshTime] = useState(null);

    // Calculate new entries count
    const newEntriesCount = useMemo(() => {
        return gdsrEntries.filter(entry => entry.isNew).length;
    }, [gdsrEntries]);

    // Fuse.js for fuzzy search
    const fuse = useMemo(() => new Fuse(gdsrEntries, {
        keys: ['token_id', 'local_no', 'name', 'father_name', 'cnic', 'phone_no', 'status'],
        threshold: 0.3,
        includeScore: true
    }), [gdsrEntries]);

    const fetchEntries = useCallback(async () => {
        try {
            const response = await axios.post('https://chaman.gob.pk/eac_backend/localdom.php', {
                action: 'get_records',
            });

            if (Array.isArray(response.data)) {
                const now = new Date();
                setLastRefreshTime(now);

                // Parse and attach timestamp, then sort by newest date+time
                const sortedEntries = response.data
                    .map(entry => {
                        // Try to parse the date reliably
                        const timestamp = new Date(entry.date);
                        return {
                            ...entry,
                            timestamp: isNaN(timestamp.getTime()) ? new Date(0) : timestamp
                        };
                    })
                    .sort((a, b) => b.timestamp - a.timestamp); // Newest first

                setGdsrEntries(prevEntries => {
                    const existingIds = new Set(prevEntries.map(item => item.token_id));

                    const newEntries = sortedEntries
                        .filter(entry => !existingIds.has(entry.token_id))
                        .map(entry => ({ ...entry, isNew: true }));

                    const existingEntries = prevEntries
                        .map(entry => ({ ...entry, isNew: false }));

                    return [...newEntries, ...existingEntries];
                });
            } else {
                setGdsrEntries([]);
                console.error('Expected array but got:', response.data);
            }
        } catch (err) {
            setError('Failed to fetch Local Domicile entries');
            console.error(err);
        } finally {
            setLoading(false);
            setIsRefreshing(false);
        }
    }, []);


    useEffect(() => {
        // Initial load with small delay to show loading state
        const timer = setTimeout(() => {
            fetchEntries();
        }, 5);

        return () => clearTimeout(timer);
    }, [fetchEntries]);

    const refreshData = () => {
        setIsRefreshing(true);
        fetchEntries();
    };

    const filteredEntries = useMemo(() => {
        if (!searchTerm) return gdsrEntries;
        return fuse.search(searchTerm).map(result => result.item);
    }, [gdsrEntries, searchTerm, fuse]);

    const handleDelete = async (token_id) => {
        if (window.confirm(`Are you sure you want to delete the entry with Token ID: ${token_id}?`)) {
            try {
                const response = await axios.post('https://chaman.gob.pk/eac_backend/localdom.php', {
                    action: 'delete',
                    token_id,
                });
                if (response.data.status === 'success') {
                    setGdsrEntries(prev => prev.filter(entry => entry.token_id !== token_id));
                } else {
                    throw new Error(response.data.message || 'Failed to delete');
                }
            } catch (error) {
                alert(`Error: ${error.message}`);
            }
        }
    };

    const handleEdit = (entry) => {
        navigate('/dashboard/edit-local-domicile', { state: { entry } });
    };

    const handlePrint = async (entry) => {
        const doc = new jsPDF();
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();

        // Draw decorative border
        doc.setLineWidth(1.0);
        doc.setDrawColor(0, 0, 255);
        doc.setDrawColor(0, 0, 0);
        doc.rect(10, 10, pageWidth - 20, pageHeight - 20, 'S');

        // Add logo image and text
        doc.setFontSize(10);
        const imageWidth = 0.8 * 25.4;
        const imageHeight = 0.6 * 25.4;
        const xCenter = (pageWidth - imageWidth) / 2;
        doc.addImage(logoImage, 'PNG', xCenter, 20, imageWidth, imageHeight);

        doc.setFontSize(21.5);
        doc.setFont("normal");
        doc.text('LOCAL DOMICILE', 72, 47);

        doc.setFontSize(15);
        doc.setFont("normal");
        doc.text('OFFICE OF THE DEPUTY COMMISSIONER CHAMAN', 40, 55);

        doc.setLineWidth(0.5); // Adjust thickness
        doc.setDrawColor(0, 0, 0);
        doc.line(14, 58, 195, 58);

        doc.setFontSize(11);
        doc.text(`Domicile No.: ${entry.local_no}`, 14, 63);
        doc.text(`Date: ${entry.date}`, 168, 63);


        // const statusText = `Status: ${entry.status}`;
        // doc.setFontSize(12);
        // doc.setFont("bold");
        // doc.setFont(undefined, 'underline');
        // doc.text(statusText, pageWidth / 2, 63, { align: "center" });


        const subjectX = 50;
        const maxSubjectWidth = doc.internal.pageSize.width - 70;
        const subjectLines = doc.splitTextToSize(entry.subject || '', maxSubjectWidth);
        doc.setFontSize(11);
        doc.setFont("normal");
        doc.text(subjectLines, subjectX, 95);

        const maxWidth = doc.internal.pageSize.width - 70;
        const lines = doc.splitTextToSize(entry.statement || '', maxWidth);
        const statementY = 50 + (subjectLines.length * 8);
        doc.text(lines, 50, statementY);
        const tableY = statementY + (lines.length * 5) + 2;

        const tableData = [
            ["Name", entry.name],
            ["Father Name", entry.father_name],
            ["CNIC", entry.cnic],
            ["Address.", entry.address],
            ["District.", entry.district],
            ["Province.", entry.province],
            ["Date of Arrival.", entry.date_of_arrival],
            ["Marital Status.", entry.marital_status],
            ["Wife Name.", entry.wife_name],
            ["Husband Name.", entry.husband_name],
            ["Sons Names / Ages.", entry.sons_names_ages],
            ["Daughters Names / Ages.", entry.daughters_names_ages],
            ["Trade Occupation.", entry.trade_of_occupation],
            ["Marks of Identification.", entry.marks_of_identification],
        ];

        doc.autoTable({
            body: tableData,
            startY: tableY,
            theme: 'grid',
            headStyles: { fillColor: [0, 0, 0], textColor: [255, 255, 255] },
            styles: {
                fontSize: 11,
                textColor: [0, 0, 0],
                lineColor: [0, 0, 0],
            },
            columnStyles: {
                0: { cellWidth: 50, halign: 'left' },
                1: { fontStyle: 'normal' },
            },
        });

       // Footer Section (Adjust dynamically based on the table)
       const footerY = Math.max(doc.autoTable.previous.finalY + 5, pageHeight - 80);

       // Text position for "DEPUTY COMMISSIONER"
       const deputyTextX = 148;  // X position for "DEPUTY COMMISSIONER"
       const deputyTextY = footerY; // Y position for "DEPUTY COMMISSIONER"

       // Signature Image Position
       const signatureWidth = 0.8 * 25;  // Width of the signature (in cm)
       const signatureHeight = 0.6 * 25;  // Height of the signature (in cm)

       // X and Y position for the signature image
       const signatureX = 163;  // X position for the signature (align with "DEPUTY COMMISSIONER")
       const signatureY = deputyTextY - signatureHeight - 1;  // Y position for the signature (above the "DEPUTY COMMISSIONER" text)

       // Add the signature image
    //    doc.addImage(signatureImage, 'PNG', signatureX, signatureY, signatureWidth, signatureHeight);

       // Add "DEPUTY COMMISSIONER" and "CHAMAN" text
       doc.setFontSize(10).setFont('helvetica', 'normal');  // Same font as the statement
       doc.text('DEPUTY COMMISSIONER', deputyTextX, deputyTextY);
       doc.text('CHAMAN', 163, footerY + 5);


        // const barcodeCanvas = document.createElement('canvas');
        // JsBarcode(barcodeCanvas, entry.token_id, { width: 1, height: 15, displayValue: false });
        // const barcodeDataURL = barcodeCanvas.toDataURL('image/png');
        // const barcodeY = pageHeight - 65; // Adjust Y position to prevent overlap
        // doc.addImage(barcodeDataURL, 'PNG', 82, barcodeY, 50, 20);

         // QR Code Generation
         const qrData = `Domicile of ${entry.name} has been verified and issued by the Office of the Deputy Commissioner, Chaman.`;
         const qrCanvas = document.createElement('canvas');
 
         await QRCode.toCanvas(qrCanvas, qrData, { width: 100 }); // Increased width for better scanning
         const qrDataURL = qrCanvas.toDataURL('image/png');
 
         const qrX = 100; // Adjust X position
         const qrY = pageHeight - 80; // Adjust Y position
 
         // Adding QR Code to PDF
         doc.addImage(qrDataURL, 'PNG', qrX, qrY, 20, 20); // Adjusted size for better readability
 

        window.open(doc.output('bloburl'));
    };

    const Row = ({ index, style }) => {
        const entry = filteredEntries[index];
        if (!entry) return null;

        return (
            <div style={style} className="table-row">
                <div className="table-cell">{entry.token_id}</div>
                <div className="table-cell">{entry.date}</div>
                <div className="table-cell">{entry.name}</div>
                <div className="table-cell">{entry.father_name}</div>
                <div className="table-cell">{entry.cnic}</div>
                <div className="table-cell actions btn-group">
                    <button onClick={() => handlePrint(entry)} title="Print" className="print-btn">
                        <FontAwesomeIcon icon={faPrint} />
                    </button>
                    <button onClick={() => handleEdit(entry)} title="Edit" className="edit-btn">
                        <FontAwesomeIcon icon={faEdit} />
                    </button>
                    <button onClick={() => handleDelete(entry.token_id)} title="Delete" className="delete-btn">
                        <FontAwesomeIcon icon={faTrash} />
                    </button>
                </div>

            </div>
        );
    };

    if (loading) return (
        <div className="loading-container">
            <div className="modern-loading">
                <div className="loading-wave">
                    <div className="loading-bar"></div>
                    <div className="loading-bar"></div>
                    <div className="loading-bar"></div>
                    <div className="loading-bar"></div>
                </div>
                <p className="loading-text">Loading ...</p>
            </div>
        </div>
    );

    if (error) return (
        <div className="error-container">
            <p>Error: {error}</p>
            <button onClick={() => window.location.reload()}>Retry</button>
        </div>
    );

    return (
        <div className="certificate-dashboard">
            <div className="dashboard-header">
                <div className="header-left">
                    {/* <h2>Local Certificate Entries</h2> */}
                    <span className="entries-count">Total Records {gdsrEntries.length}</span>
                </div>

                <div className="header-right">
                    <div className="search-refresh-wrapper">
                        <button
                            onClick={refreshData}
                            className="refresh-button"
                            disabled={isRefreshing}
                            aria-label="Refresh data"
                        >
                            <FontAwesomeIcon
                                icon={faSyncAlt}
                                spin={isRefreshing}
                                className={`refresh-icon ${isRefreshing ? 'spinning' : ''}`}
                            />
                        </button>

                        <div className="search-bar">
                            <FontAwesomeIcon icon={faSearch} className="search-icon" />
                            <input
                                type="text"
                                placeholder="Search domiciles..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                            />
                            {searchTerm && (
                                <button className="clear-search" onClick={() => setSearchTerm('')}>
                                    ×
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>




            <div className="table-container">
                <div className="table-header">
                    <div className="header-cell">Token ID</div>
                    <div className="header-cell">Date</div>
                    <div className="header-cell">Name</div>
                    <div className="header-cell">F/H Name</div>
                    <div className="header-cell">CNIC</div>
                    <div className="header-cell">Actions</div>
                </div>

                <div className="table-body">
                    {filteredEntries.length > 0 ? (
                        <AutoSizer>
                            {({ height, width }) => (
                                <List
                                    height={height}
                                    itemCount={filteredEntries.length}
                                    itemSize={45}
                                    width={width}
                                >
                                    {Row}
                                </List>
                            )}
                        </AutoSizer>
                    ) : (
                        <div className="empty-state">
                            <p>No entries found {searchTerm ? `matching "${searchTerm}"` : 'available'}</p>
                        </div>
                    )}
                </div>

                <div className="table-footer">
                    Showing {filteredEntries.length} of {gdsrEntries.length} entries
                </div>
            </div>
        </div>
    );
};

export default Localdomlist;